import {
	CoverageBar,
	CoveragePercentage,
	CoverageStatusLabel,
	type CoverageValue,
} from '../../Coverage/Coverage';
import type { ExecutionPeriodTable } from '../../../utils/maintenanceExecutionMapper';
import styles from './ExecutionTable.module.css';

export interface ExecutionTableProps {
	data: ExecutionPeriodTable;
}

const EMPTY = '—';
const UNIT = 'kg/ha';

const valueFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

const totalFormatter = new Intl.NumberFormat('pt-BR', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const formatValue = (value: number | null) =>
	value == null || !Number.isFinite(value) ? EMPTY : valueFormatter.format(value);

const CoverageCells = ({ coverage }: { coverage: CoverageValue | null }) => (
	<>
		<td className={styles.coverageCell}>
			{coverage ? <CoverageBar percentage={coverage.percentage} /> : EMPTY}
		</td>
		<td className={styles.resultCell}>
			{coverage ? (
				<span className={styles.result}>
					<CoveragePercentage value={coverage} />
					<CoverageStatusLabel status={coverage.status} />
				</span>
			) : (
				EMPTY
			)}
		</td>
	</>
);

export const ExecutionTable = ({ data }: ExecutionTableProps) => (
	<div className={styles.wrapper}>
		<table className={styles.table}>
			<thead>
				<tr>
					<th className={styles.headerCell}>PRODUTO</th>
					<th className={`${styles.headerCell} ${styles.alignRight}`}>PREVISTO</th>
					<th className={`${styles.headerCell} ${styles.alignRight}`}>APLICADO</th>
					<th className={`${styles.headerCell} ${styles.coverageHeaderCell}`}>COBERTURA</th>
					<th className={`${styles.headerCell} ${styles.alignRight}`}>DO PREVISTO</th>
				</tr>
			</thead>
			{data.groups.map((group) => (
				<tbody key={group.date} className={styles.group}>
					<tr>
						<td colSpan={5} className={styles.groupCell}>
							<div className={styles.groupHeader}>
								<span>
									<span className={styles.groupStrong}>{group.date}</span>
									<span className={styles.groupMuted}>{group.applicationNumber}ª aplicação</span>
								</span>
								<span>
									<span className={styles.groupStrong}>{totalFormatter.format(group.applied)}</span>
									<span className={styles.groupMuted}>
										de {totalFormatter.format(group.planned)} {UNIT}
									</span>
								</span>
							</div>
						</td>
					</tr>
					{group.lines.map((line) => (
						<tr key={line.id} className={styles.row}>
							<td className={styles.productCell}>{line.name}</td>
							<td className={`${styles.plannedCell} ${styles.alignRight}`}>
								{formatValue(line.planned)}
							</td>
							<td className={`${styles.appliedCell} ${styles.alignRight}`}>
								{formatValue(line.applied)}
							</td>
							<CoverageCells coverage={line.coverage} />
						</tr>
					))}
				</tbody>
			))}
			<tfoot>
				<tr className={styles.totalRow}>
					<td className={styles.totalLabel}>Total do mês</td>
					<td className={`${styles.plannedCell} ${styles.alignRight}`}>
						{totalFormatter.format(data.totals.planned)}
					</td>
					<td className={`${styles.totalValue} ${styles.alignRight}`}>
						{totalFormatter.format(data.totals.applied)}
					</td>
					<CoverageCells coverage={data.totals.coverage} />
				</tr>
			</tfoot>
		</table>
	</div>
);
